/* eslint-disable jsx-a11y/click-events-have-key-events */
import { DirittoAppBridge } from 'libs/DirittoAppBridge';
import { useClient } from 'ui-hooks/useClient';
import { MAX_INT_32 } from '@diritto/common/base/constants';
import {
  ClickAction,
  ClickTargetType,
  TrackerPosition,
  TrackingEventType,
} from '@diritto/common/tracker/tracking-event.constant';
import styled from '@emotion/styled';
import { usePromotionEligibilityCheck } from 'app-components/promotions/UsePromotionEligibilityCheck';
import { isPC, PCOffset } from 'constants/layout';
import { useAuthState } from 'contexts/AuthProvider';
import { useTrackingState } from 'contexts/TrackingProvider';
import { useRouter } from 'next/navigation';
import { useGetAccountMeQuery } from 'queries/account/useGetAccountMeQuery';
import { useGetPromotionStatusQuery } from 'queries/promotions/useGetPromotionStatusQuery';
import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { useViewPort } from 'ui-hooks/useViewPort';
import { configure } from 'utils/configure';

const PROMOTION_ID = 3;

const REWARD_TOTAL_BULB_COUNT_REWARD_MAP = {
  0: '0P',
  1: '300P',
  2: '500P',
  3: '700P',
  4: '1,000P',
  5: '1,500P',
};

const REWARD_LEVEL_RANGE_MAP = {
  0: [0, 10000],
  1: [10001, 50000],
  2: [50001, 100000],
  3: [100001, 500000],
  4: [500001, 1000000],
  5: [1000001, MAX_INT_32],
};

const stages = [
  {
    level: 0,
    images: {
      success: `${configure.staticPrefix}/images/promotions/2024-santa-ritto/stage0_success.png`,
      challenge: `${configure.staticPrefix}/images/promotions/2024-santa-ritto/stage0_challenge.png`,
    },
  },
  {
    level: 1,
    images: {
      fadeout: `${configure.staticPrefix}/images/promotions/2024-santa-ritto/stage1_fadeout.png`,
      challenge: `${configure.staticPrefix}/images/promotions/2024-santa-ritto/stage1_challenge.png`,
      success: `${configure.staticPrefix}/images/promotions/2024-santa-ritto/stage1_success.png`,
    },
  },
  {
    level: 2,
    images: {
      fadeout: `${configure.staticPrefix}/images/promotions/2024-santa-ritto/stage2_fadeout.png`,
      challenge: `${configure.staticPrefix}/images/promotions/2024-santa-ritto/stage2_challenge.png`,
      success: `${configure.staticPrefix}/images/promotions/2024-santa-ritto/stage2_success.png`,
    },
  },
  {
    level: 3,
    images: {
      fadeout: `${configure.staticPrefix}/images/promotions/2024-santa-ritto/stage3_fadeout.png`,
      challenge: `${configure.staticPrefix}/images/promotions/2024-santa-ritto/stage3_challenge.png`,
      success: `${configure.staticPrefix}/images/promotions/2024-santa-ritto/stage3_success.png`,
    },
  },
  {
    level: 4,
    images: {
      fadeout: `${configure.staticPrefix}/images/promotions/2024-santa-ritto/stage4_fadeout.png`,
      challenge: `${configure.staticPrefix}/images/promotions/2024-santa-ritto/stage4_challenge.png`,
      success: `${configure.staticPrefix}/images/promotions/2024-santa-ritto/stage4_success.png`,
    },
  },
];

export const SantaRitto2024 = () => {
  const { isApp } = useClient();
  const { tracker } = useTrackingState();
  const { user } = useAuthState();
  const router = useRouter();

  let sections;
  if (user) {
    sections = ['participate', 'my', 'reward'];
  } else {
    sections = ['participate', 'reward'];
  }

  const { data: userData } = useGetAccountMeQuery();
  const { data } = useGetPromotionStatusQuery(PROMOTION_ID);
  const {
    handleMarketingAgreement,
    handleRidiSSoLink,
    handleUserCertification,
    isNotificationEnabled,
    isUserCertificated,
    ridiSsoLinked,
  } = usePromotionEligibilityCheck();

  const [currentSection, setCurrentSection] = useState(sections[0]);

  const variant = {
    stages: {
      0: `absolute top-[1324px] left-[653px]`,
      1: `absolute top-[995px] left-[96px]`,
      2: `absolute top-[702px] left-[688px]`,
      3: `absolute top-[497px] left-[207px]`,
      4: `absolute top-[80px] left-[322px]`,
    },
  };

  const handleTabClick = useCallback(
    (section: string) => {
      tracker.track(TrackingEventType.Click, {
        label: section,
        action: ClickAction.EventButton,
        clickType: ClickTargetType.Menu,
        trackerExtraInfo: { trackerPosition: TrackerPosition.Promotion },
      });
      router.push(`#${section}`);
      setCurrentSection(section);
    },
    [router, tracker],
  );

  useEffect(() => {
    tracker.track(TrackingEventType.ViewPromotion, { promotionId: PROMOTION_ID, slug: '2024-santa-ritto' });
  }, [tracker]);

  const now = new Date();
  const todayStart = new Date(now.getFullYear(), now.getMonth(), now.getDate());
  const myTodayBulbCount = data?.participates.filter(
    participate => new Date(participate.createdAt) >= todayStart,
  ).length;
  const myTotalBulbCount = data?.participates.length ?? 0;
  const totalBulbCount = data?.totalParticipateCount ?? 0;

  const userCreatedAt = new Date(userData?.user?.createdAt ?? 0);
  const eventStartsAt = new Date(2024, 12 - 1, 18);
  const isNewUser = userCreatedAt.getTime() >= eventStartsAt.getTime();

  const rewardLevel = useMemo(
    () =>
      +(Object.keys(REWARD_LEVEL_RANGE_MAP).find(
        key =>
          totalBulbCount >= REWARD_LEVEL_RANGE_MAP[key as unknown as keyof typeof REWARD_LEVEL_RANGE_MAP][0] &&
          totalBulbCount <= REWARD_LEVEL_RANGE_MAP[key as unknown as keyof typeof REWARD_LEVEL_RANGE_MAP][1],
      ) as unknown as keyof typeof REWARD_LEVEL_RANGE_MAP) as 0 | 1 | 2 | 3 | 4 | 5,
    [totalBulbCount],
  );
  const { width: screenWidth } = useViewPort();
  const rewardPointString = REWARD_TOTAL_BULB_COUNT_REWARD_MAP[rewardLevel];
  const [rewardLevelStart, rewardLevelEnd] = REWARD_LEVEL_RANGE_MAP[rewardLevel];
  const rewardProgressPercent = useMemo(
    () => (totalBulbCount - rewardLevelStart) / (rewardLevelEnd - rewardLevelStart),
    [rewardLevelEnd, rewardLevelStart, totalBulbCount],
  );
  const contentWidth = useMemo(
    () => (screenWidth > PCOffset ? Math.min(screenWidth, 730) : screenWidth),
    [screenWidth],
  );
  const scale = useMemo(() => contentWidth / 1110, [contentWidth]);
  const isRewardUser = useMemo(
    () => (isNewUser ? myTotalBulbCount >= 3 : myTotalBulbCount >= 5),
    [isNewUser, myTotalBulbCount],
  );
  const myImageFilename = rewardLevel >= 5 ? 'my_end' : 'my';
  const height = user ? 17397 : 16101;

  const scrollToHash = () => {
    const hash = window.location.hash.replace('#', '');
    if (hash) {
      const section = document.getElementById(hash);
      if (section) {
        section.scrollIntoView({ behavior: 'smooth', block: 'start' });
      }
    }
  };

  useEffect(() => {
    setTimeout(() => {
      scrollToHash();
    }, 1000);
  }, []);

  return (
    <div
      className="flex flex-col relative bg-[#15181E]"
      style={{ width: `${contentWidth}px`, maxHeight: `${height * scale}px` }}>
      <div className="flex flex-col items-center gap-0 w-full flex-wrap relative">
        <img
          alt="title"
          loading="eager"
          src={`${configure.staticPrefix}/images/promotions/2024-santa-ritto/context.png`}
        />
      </div>

      <div className="sticky top-0 w-full z-[1000]">
        <div className="relative scroll-mt-[70px] bg-[#C72424] w-full flex justify-center">
          <div className="flex justify-around items-center w-full">
            {sections.map(section => (
              // eslint-disable-next-line jsx-a11y/click-events-have-key-events,jsx-a11y/no-static-element-interactions
              <div
                key={section}
                className="flex flex-col relative items-center w-full cursor-pointer py-[5%] text-[100%] pc:text-[150%]"
                onClick={() => handleTabClick(section)}>
                <div className="text-white text-center leading-tight">
                  {section === 'participate' && (
                    <>
                      전체 누적
                      <br />
                      전구 개수
                    </>
                  )}
                  {section === 'my' && (
                    <>
                      나의 전구
                      <br />
                      모으기
                    </>
                  )}
                  {section === 'reward' && (
                    <>
                      리워드
                      <br />
                      안내
                    </>
                  )}
                </div>
                {section === currentSection && <div className="absolute bottom-0 w-full h-[4%] bg-white" />}
              </div>
            ))}
          </div>
        </div>
      </div>

      <StyledScaleWrapper className={`flex flex-1 w-[1110px] h-[${height}px]`} scale={scale}>
        <div className="flex flex-col items-center gap-0 w-full flex-wrap relative">
          <style jsx>{`
            body div {
              -webkit-transform: translate3d(0, 0, 0);
            }
            @keyframes marquee {
              0% {
                transform: translateX(100%);
              }
              100% {
                transform: translateX(-100%);
              }
            }
          `}</style>
          <div
            className="relative scroll-mt-[70px] w-full flex gap-0 flex-wrap justify-center transform"
            id="participate">
            {rewardLevel < 5 ? (
              <img
                className="w-full h-auto"
                alt="howto"
                src={`${configure.staticPrefix}/images/promotions/2024-santa-ritto/howto.png`}
              />
            ) : (
              <img
                className="w-full h-auto"
                alt="howto"
                src={`${configure.staticPrefix}/images/promotions/2024-santa-ritto/howto_end.png`}
              />
            )}

            <div className="absolute w-[970px] left-[70px] top-[729px]">
              <img
                className="w-[970px] left-[70px]"
                sizes="970px"
                alt=""
                src={`${configure.staticPrefix}/images/promotions/2024-santa-ritto/pb${rewardLevel}.png`}
              />
              {rewardLevel < 5 && (
                <div className="absolute left-[60px] top-[343px] w-[849px] h-[30px] bg-[#d8d8d8] rounded-[60px]">
                  <StyledProgressBar progress={rewardProgressPercent}>
                    <StyledProgressBarCircle progress={rewardProgressPercent} />
                  </StyledProgressBar>
                </div>
              )}
              {rewardLevel < 5 && (
                <div className="absolute top-[168px] left-1/2 transform -translate-x-1/2 text-[84px] font-black text-black leading-[105%]">
                  {totalBulbCount}
                </div>
              )}
              {rewardLevel < 5 ? (
                <div className="absolute top-[566px] w-[970px] h-[160px] flex justify-center items-center overflow-hidden z-[2]">
                  <div className="absolute text-[40px] text-white animate-[marquee_10s_linear_infinite]">
                    현재&nbsp;
                    <span className="text-[#FF3131] font-pretendard text-[40px] font-bold leading-[129%]">
                      {data?.totalParticipantsCount ?? 0}
                    </span>
                    &nbsp;명이 참여하고 있어요. 함께 도전해봐요!
                  </div>
                </div>
              ) : (
                <div className="absolute top-[566px] w-[970px] h-[160px] flex justify-center items-center overflow-hidden z-[2]">
                  <div className="absolute text-[40px] text-white animate-[marquee_10s_linear_infinite]">
                    누적 전구 개수&nbsp;
                    <span className="text-[#FF3131] font-pretendard text-[40px] font-bold leading-[129%]">
                      1,000,000
                    </span>
                    &nbsp;달성! 🎉🎉🎉
                  </div>
                </div>
              )}
            </div>
          </div>
          {/* 트리 전구 이미지 */}
          <div className="relative w-full flex gap-0 flex-wrap justify-center overflow-hidden">
            <img
              className="w-full h-auto object-fill"
              alt="dashboard"
              src={`${configure.staticPrefix}/images/promotions/2024-santa-ritto/dashboard.png`}
            />
            {stages.map(stage => {
              let imageSrc = stage.images.fadeout; // Default to fadeout

              if (rewardLevel === stage.level) {
                imageSrc = stage.images.challenge;
              } else if (rewardLevel > stage.level) {
                imageSrc = stage.images.success || stage.images.challenge;
              }

              return (
                <div
                  key={stage.level}
                  className={`flex w-[300px] h-[300px] items-center justify-center ${variant.stages[stage.level as keyof typeof variant.stages]}`}
                  style={{ width: stage.level === 4 ? `450px` : `300px` }}>
                  {imageSrc && <img src={imageSrc} alt={`Stage ${stage.level}`} />}
                </div>
              );
            })}
            <div className="absolute top-[2123px] w-full text-center font-black text-[105px] leading-[84.642px] text-black">
              {rewardPointString}
            </div>
          </div>
          {/* 나의 전구 모으기 */}
          {user && (
            <div
              className="relative scroll-mt-[70px] bg-white w-full flex gap-0 flex-wrap justify-center overflow-hidden"
              id="my">
              <img
                className="relative w-full h-auto"
                alt="my"
                src={`${configure.staticPrefix}/images/promotions/2024-santa-ritto/${myImageFilename}.png`}
              />
              <div
                className="flex justify-center absolute top-[795px]  left-[218px] w-[200px] text-center font-black text-[120px] leading-[80%] text-black"
                style={{ color: isRewardUser ? 'black' : '#C72424' }}>
                {myTotalBulbCount}
              </div>
              {!isRewardUser && (
                <div
                  className="flex justify-center absolute top-[890px] left-[168px] w-[300px] text-center font-medium text-[27px] leading-[65px] text-black"
                  style={{ color: isRewardUser ? 'black' : '#C72424' }}>
                  리워드 지급 조건 미달성
                </div>
              )}
              <div className="flex items-end absolute top-[795px] left-[745px] text-center font-black text-[120px] leading-[80%] text-black">
                <p>{myTodayBulbCount ?? 0}</p>
                <span className="text-[55px] font-bold leading-tight">/3</span>
              </div>
              {/* eslint-disable-next-line jsx-a11y/no-static-element-interactions */}
              <div
                onClick={() => {
                  if (rewardLevel >= 5) {
                    alert('종료된 이벤트입니다.');
                    return;
                  }
                  if (isApp) {
                    DirittoAppBridge.resetToHome();
                  } else {
                    router.push('/');
                  }
                }}
                className="flex absolute top-[1433px] w-[970px] left-[70px] h-[180px]"
              />
              {/* eslint-disable-next-line jsx-a11y/no-static-element-interactions */}
              <div
                onClick={async () => {
                  if (rewardLevel >= 5) {
                    alert('종료된 이벤트입니다.');
                    return;
                  }

                  const image = await fetch(`/promotions/2024-santa-ritto/live?bc=${totalBulbCount}`);
                  const file = new File([await image.blob()], '2024-santa-ritto.png', { type: 'image/png' });
                  navigator.share({
                    title: '크리스마스 선물 받으러 올래?',
                    text: '다함께 전구를 모으면 전원 최대 1,500P! #산타리토의선물대작전 #디리토 #전원리디포인트',
                    url: 'https://www.diritto.co.kr/promotions/2024-santa-ritto',
                    files: [file],
                  });
                }}
                className="flex absolute top-[1653px] w-[970px] left-[70px] h-[180px]"
              />
            </div>
          )}
          {!user && (
            <img
              className="relative w-full h-auto"
              alt="my"
              src={`${configure.staticPrefix}/images/promotions/2024-santa-ritto/my_logout.png`}
            />
          )}
          <div
            id="todo"
            className="relative scroll-mt-[70px] w-full flex gap-0 flex-wrap justify-center overflow-hidden">
            <div className="absolute top-[51%] z-[1] flex w-full flex-row gap-[10%] justify-center">
              <img
                className="cursor-pointer max-w-[15%] object-contain"
                alt="todo_1"
                src={
                  isUserCertificated
                    ? `${configure.staticPrefix}/images/promotions/2024-santa-ritto/todo_1_done.png`
                    : `${configure.staticPrefix}/images/promotions/2024-santa-ritto/todo_1.png`
                }
                onClick={isUserCertificated ? undefined : handleUserCertification}
              />
              <img
                className="cursor-pointer max-w-[15%] object-contain"
                alt="todo_2"
                src={
                  isNotificationEnabled
                    ? `${configure.staticPrefix}/images/promotions/2024-santa-ritto/todo_2_done.png`
                    : `${configure.staticPrefix}/images/promotions/2024-santa-ritto/todo_2.png`
                }
                onClick={isNotificationEnabled ? undefined : handleMarketingAgreement}
              />
              <img
                className="cursor-pointer max-w-[15%] object-contain"
                alt="todo_3"
                src={
                  ridiSsoLinked
                    ? `${configure.staticPrefix}/images/promotions/2024-santa-ritto/todo_3_done.png`
                    : `${configure.staticPrefix}/images/promotions/2024-santa-ritto/todo_3.png`
                }
                onClick={ridiSsoLinked ? undefined : handleRidiSSoLink}
              />
            </div>
            <img
              className="relative w-full h-auto"
              alt="requirements"
              src={`${configure.staticPrefix}/images/promotions/2024-santa-ritto/requirements.png`}
            />
          </div>
          <div
            className="relative scroll-mt-[70px] w-full flex gap-0 flex-wrap justify-center overflow-hidden"
            id="reward">
            <img
              className="relative w-full h-auto"
              alt="points"
              src={`${configure.staticPrefix}/images/promotions/2024-santa-ritto/points.png`}
            />
          </div>
          <div className="relative w-full h-auto flex flex-wrap">
            <img alt="readme" src={`${configure.staticPrefix}/images/promotions/2024-santa-ritto/readme.png?v=2`} />
          </div>
        </div>
      </StyledScaleWrapper>
    </div>
  );
};

const StyledScaleWrapper = styled.div<{ scale: number }>`
  transform: scale(${({ scale = 0.3 }) => scale});
  transform-origin: top left;
  position: relative;

  @media (${isPC}) {
    transform: scale(0.6576576577);
  }
`;

const StyledProgressBar = styled.div<{ progress: number }>`
  position: absolute;
  min-width: 4.6%;
  width: ${({ progress }) => `${progress * 100}%`};
  height: 100%;
  background-color: #c72424;
  border-radius: 60px;
  transition: width 0.3s ease-in-out;
`;

const StyledProgressBarCircle = styled.div<{ progress: number }>`
  position: absolute;
  width: 16px;
  height: 16px;
  background-color: white;
  border-radius: 100%;
  top: 50%;
  transform: translateY(-50%);
  right: 8px;
  transition: left 0.3s ease-in-out;
`;
